import { useEffect, useRef } from "react";
import { kontakt } from "@/mocks/lokacija";
import { useOpeningStatus } from "@/hooks/useOpeningStatus";

export default function Hero() {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const { isOpen, statusText, todayHours } = useOpeningStatus();

  useEffect(() => {
    const element = parallaxRef.current;
    if (!element) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      const offset = Math.min(window.scrollY, window.innerHeight);
      element.style.transform = `translate3d(0, ${offset * 0.2}px, 0)`;
    };

    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section id="domov" className="relative min-h-[100svh] w-full overflow-hidden bg-primary-950">
      <div ref={parallaxRef} className="absolute inset-0 will-change-transform">
        <div className="h-[112%] w-full overflow-hidden bg-primary-950">
          <img
            src="https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/1b833b8a41936e010c811757096dea47.png"
            alt="Terasa in zunanjost bara Stanka na Ptuju v večernih urah"
            title="Bar Stanka Ptuj – terasa in vhod v lokal"
            className="h-full w-full object-cover object-[50%_45%] sm:object-top"
          />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/30 to-primary-950/95" />

      <div className="pointer-events-none absolute inset-4 z-[5] hidden border border-background-50/12 md:block lg:inset-6" />
      <div className="pointer-events-none absolute left-4 top-4 z-[5] hidden h-6 w-6 border-l border-t border-accent-400/40 md:block lg:left-6 lg:top-6" />
      <div className="pointer-events-none absolute bottom-24 right-4 z-[5] hidden h-6 w-6 border-b border-r border-accent-400/40 md:block lg:right-6" />

      <div className="relative z-10 flex min-h-[100svh] w-full flex-col justify-end px-5 pb-44 pt-28 md:px-10 md:pb-40 md:pt-44 lg:px-12">
        <div className="mx-auto w-full max-w-content text-center">
          <div className="anim-fade delay-200 flex items-center justify-center gap-3 sm:gap-4">
            <span className="h-px w-6 bg-accent-400/60 sm:w-16" />
            <span className="eyebrow whitespace-nowrap text-accent-300">Zadružni trg 12D · Ptuj</span>
            <span className="h-px w-6 bg-accent-400/60 sm:w-16" />
          </div>

          <h1 className="anim-rise delay-300 mt-5 font-heading text-[11.5vw] leading-[0.94] font-medium tracking-[0.06em] text-background-50 sm:mt-6 sm:text-[9vw] sm:tracking-[0.08em] md:text-[7vw] lg:text-[76px]">
            BAR STANKA
          </h1>

          <div className="anim-fade delay-400 mt-5 flex items-center justify-center gap-3 sm:mt-6">
            <span className="h-px w-8 bg-accent-400/40 sm:w-12" />
            <span className="h-[5px] w-[5px] rotate-45 bg-accent-400/90" />
            <span className="h-px w-8 bg-accent-400/40 sm:w-12" />
          </div>

          <p className="anim-rise delay-400 mx-auto mt-4 max-w-[620px] font-heading text-lg italic text-accent-200 md:text-[22px]">
            Vaš trenutek sprostitve na Ptuju.
          </p>

          <p className="anim-rise delay-500 mx-auto mt-4 max-w-[520px] text-[13.5px] leading-relaxed text-background-100/85 md:text-[14.5px]">
            Prijetno vzdušje, dobra kava in prostor, kjer si lahko vzamete čas
            zase ali za druženje s prijatelji.
          </p>

          <div className="anim-fade delay-500 mt-6 flex justify-center">
            <span className="inline-flex items-center gap-2.5 rounded-full border border-background-50/25 bg-background-50/10 px-4 py-2 font-label text-[10px] font-semibold uppercase tracking-[0.18em] text-background-50 backdrop-blur-sm">
              <span className="relative flex h-2 w-2 items-center justify-center">
                <span
                  className={`absolute h-2 w-2 rounded-full ${
                    isOpen ? "bg-accent-400/50" : "bg-background-300/40"
                  } ${isOpen ? "animate-ping" : ""}`}
                />
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    isOpen ? "bg-accent-400" : "bg-background-300"
                  }`}
                />
              </span>
              {statusText}
              {todayHours !== "Zaprto" && (
                <>
                  <span className="hidden text-background-100/50 sm:inline">·</span>
                  <span className="hidden text-background-100/65 sm:inline">{todayHours}</span>
                </>
              )}
            </span>
          </div>

          <div className="anim-rise delay-600 mt-6 flex flex-col items-stretch gap-3 sm:mt-7 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
            <a
              href="#o-nas"
              className="btn-base w-full bg-accent-500 text-primary-950 hover:bg-background-50 sm:w-auto"
            >
              Odkrijte naš bar
              <i className="ri-arrow-down-line text-base" />
            </a>
            <a
              href={kontakt.navigacija}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-base w-full border-background-50/50 text-background-50 hover:border-background-50 hover:bg-background-50 hover:text-primary-950 sm:w-auto"
            >
              Kako do nas
              <i className="ri-map-pin-line text-base" />
            </a>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 border-t border-background-50/15 bg-primary-950/55 backdrop-blur-[4px]">
        <div className="w-full px-5 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 items-center gap-3 py-4 sm:grid-cols-3 sm:gap-6 sm:py-5">
            <a
              href={kontakt.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-background-100/75 transition-colors hover:text-background-50 sm:justify-start sm:text-[11px]"
            >
              <i className="ri-map-pin-line text-sm text-accent-400" />
              Zadružni trg 12D, Ptuj
            </a>

            <div className="flex items-center justify-center gap-2 border-y border-background-50/10 py-2.5 text-[10px] font-medium uppercase tracking-[0.14em] text-background-100/75 sm:border-0 sm:py-0 sm:text-[11px]">
              <i className="ri-time-line text-sm text-accent-400" />
              Pon–Čet 06–22 · Pet–Sob 06–24 · Ned 07–20
            </div>

            <div className="flex items-center justify-center gap-2 sm:justify-end">
              <a
                href={kontakt.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bar Stanka na Facebooku"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/25 text-background-100/80 transition-colors hover:border-accent-400 hover:bg-accent-500 hover:text-primary-950"
              >
                <i className="ri-facebook-fill text-sm" />
              </a>
              <a
                href={kontakt.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bar Stanka na Instagramu"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/25 text-background-100/80 transition-colors hover:border-accent-400 hover:bg-accent-500 hover:text-primary-950"
              >
                <i className="ri-instagram-line text-sm" />
              </a>
              <a
                href="#o-nas"
                aria-label="Pomaknite se do sekcije O nas"
                className="ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/10 text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950"
              >
                <i className="scroll-dot ri-arrow-down-s-line text-base" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}